import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { parseISO, format } from 'date-fns';
import { ArrowLeft, Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { DiaryLayout } from './DiaryLayout';
import { PAPER } from './theme';
import { AuthThumb, rowThumbProps } from './AuthThumb';
import { CostLogDetail } from './CostLogDetail';
import { LabourLog } from './LabourLog';
import { useVatBasis, VatBasisToggle, rowPence, totalsFor, BASIS_LABEL } from '../../components/VatBasisToggle';

const fmtGbp = (pounds) => `£${(Number(pounds) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Project Cost Log — what makes up the diary tiles. Two views:
 *   material → cost-ledger material rows (receipts) with the running total
 *   labour   → diary labour person-days costed at day rate
 * Reached by tapping the Material / Labour tile on the diary day view.
 * Read-only; admin + surveyor.
 */
export function ProjectCostLog() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { api, isAdmin, isSurveyor } = useAuth();
  const diaryPrefix = location.pathname.startsWith('/portal/diary') ? '/portal/diary' : '/diary';

  const [project, setProject] = useState(null);
  const view = searchParams.get('view') === 'labour' ? 'labour' : 'material';
  const setView = (v) => setSearchParams(v === 'labour' ? { view: 'labour' } : {}, { replace: true });
  const [data, setData] = useState(null);
  const [labourData, setLabourData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!(isAdmin || isSurveyor)) { navigate(`${diaryPrefix}/${projectId}`); return; }
    api.get('/diary/projects')
      .then(res => {
        const proj = res.data.find(p => p.id === projectId);
        if (!proj) { toast.error("You don't have access to this project's diary"); navigate(diaryPrefix); return; }
        setProject(proj);
      })
      .catch(() => navigate(diaryPrefix));
  }, [api, projectId, navigate, diaryPrefix, isAdmin, isSurveyor]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/cost-ledger/entries', { params: { project_id: projectId, limit: 200, category: 'material,plant,fuel,other' } });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load costs');
    } finally {
      setLoading(false);
    }
  }, [api, projectId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const rows = data?.entries || [];
  const [basis, setBasis] = useVatBasis();
  const totals = totalsFor(data?.totals, basis);
  const isLabour = view === 'labour';
  const headline = isLabour ? labourData?.total : (totals.non_labour ?? totals.material);
  const pendingCount = rows.filter(r => r.value_pending).length;

  const openCapture = () => {
    const params = new URLSearchParams({ project_id: projectId, from_diary: '1', locked: '1', return: `${diaryPrefix}/${projectId}/costs` });
    navigate(`/portal/capture?${params.toString()}`);
  };

  return (
    <DiaryLayout>
      <div className="max-w-3xl mx-auto px-4 pt-4 pb-32" data-testid="cost-log-page">
        <button
          type="button"
          onClick={() => navigate(`${diaryPrefix}/${projectId}`)}
          className="text-[12px] flex items-center gap-1 mb-3 hover:underline"
          style={{ color: PAPER.muted }}
          data-testid="cost-log-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Diary
        </button>

        {/* Running total */}
        <div
          className="rounded-xl px-4 py-4"
          style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, borderLeft: `6px solid ${PAPER.accent}`, boxShadow: PAPER.shadow }}
          data-testid="cost-log-header"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: PAPER.muted }}>
              {isLabour ? 'Labour to date' : `Materials & plant to date · ${BASIS_LABEL[basis]}`}
            </div>
            {!isLabour && <VatBasisToggle basis={basis} onChange={setBasis} light size="xs" testid="cost-log-vat-toggle" />}
          </div>
          <div className="flex items-end justify-between gap-3 mt-0.5">
            <div>
              <div className="text-[30px] leading-none font-bold tabular-nums" style={{ color: PAPER.ink }} data-testid="cost-log-total">
                {headline == null ? '—' : fmtGbp(headline)}
              </div>
              <div className="text-[12px] mt-1.5 truncate" style={{ color: PAPER.inkSoft }} data-testid="cost-log-project-ref">
                {project?.project_ref || ''}{project?.town_city ? ` · ${project.town_city}` : ''}
              </div>
            </div>
            {!isLabour && (
              <button
                type="button"
                onClick={openCapture}
                className="flex-shrink-0 inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-2 rounded-lg"
                style={{ background: PAPER.navy, color: PAPER.accent }}
                data-testid="cost-log-capture-btn"
              >
                <Camera className="w-4 h-4" /> Capture receipt
              </button>
            )}
          </div>
          <div className="text-[11px] mt-2 flex gap-3 flex-wrap" style={{ color: PAPER.muted }}>
            {isLabour ? (
              <>
                <span data-testid="cost-log-count">{labourData ? `${labourData.person_days} person-days · ${labourData.days_logged} ${labourData.days_logged === 1 ? 'day' : 'days'} logged` : ''}</span>
                {labourData?.missing_rate_count > 0 && <span style={{ color: '#92400E' }} data-testid="cost-log-missing-rate">{labourData.missing_rate_count} need a rate</span>}
              </>
            ) : (
              <>
                <span data-testid="cost-log-count">{data ? `${data.total} ${data.total === 1 ? 'receipt' : 'receipts'}` : ''}</span>
                {pendingCount > 0 && <span style={{ color: '#92400E' }} data-testid="cost-log-pending-count">{pendingCount} awaiting a value</span>}
              </>
            )}
          </div>
          {isLabour && labourData && (
            <div className="grid grid-cols-2 gap-2 mt-3 pt-3" style={{ borderTop: `1px solid ${PAPER.divider}` }} data-testid="cost-log-labour-split">
              <div>
                <div className="text-[9px] uppercase tracking-wide" style={{ color: PAPER.hint }}>On site · diary running total</div>
                <div className="text-[16px] font-bold tabular-nums" style={{ color: '#047857' }} data-testid="cost-log-diary-all">{fmtGbp(labourData.diary_total)}</div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-wide" style={{ color: PAPER.hint }}>Confirmed · invoiced</div>
                <div className="text-[16px] font-bold tabular-nums" style={{ color: '#0369A1' }} data-testid="cost-log-invoiced">{fmtGbp(labourData.invoiced_total ?? labourData.ledger_total)}</div>
              </div>
            </div>
          )}
        </div>

        {/* View switch */}
        <div className="flex gap-2 mt-3" data-testid="cost-log-filter">
          {[['labour', 'Labour'], ['material', 'Materials & plant']].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setView(key)}
              className="text-[12px] font-semibold px-3 py-1.5 rounded-full transition-colors"
              style={view === key
                ? { background: PAPER.navy, color: PAPER.accent }
                : { background: PAPER.card, color: PAPER.inkSoft, border: `1px solid ${PAPER.border}` }}
              data-testid={`cost-log-filter-${key}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Rows */}
        <div className="mt-3 space-y-2" data-testid="cost-log-list">
          {isLabour ? (
            <LabourLog projectId={projectId} diaryPrefix={diaryPrefix} onData={setLabourData} />
          ) : loading && !data ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" style={{ color: PAPER.accent }} /></div>
          ) : rows.length === 0 ? (
            <div className="rounded-xl px-4 py-10 text-center" style={{ background: PAPER.card, border: `1px dashed ${PAPER.border}` }} data-testid="cost-log-empty">
              <Camera className="w-7 h-7 mx-auto mb-2" style={{ color: PAPER.hint }} />
              <div className="text-[14px] font-semibold" style={{ color: PAPER.ink }}>No costs logged yet</div>
              <div className="text-[12px] mt-1" style={{ color: PAPER.muted }}>Capture a receipt from the diary and it will appear here with its running total.</div>
            </div>
          ) : rows.map(r => <CostRow key={r.id} row={r} basis={basis} onOpen={() => setSelected(r.id)} />)}
        </div>
      </div>

      {selected && (
        <CostLogDetail rowId={selected} diaryPrefix={diaryPrefix} projectId={projectId} onClose={() => setSelected(null)} />
      )}
    </DiaryLayout>
  );
}

function CostRow({ row, basis, onOpen }) {
  const when = row.invoice_date || row.created_at;
  const dateLabel = when ? format(parseISO(when), 'dd MMM yyyy') : '';
  const voided = row.status === 'void';
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-transform active:scale-[0.99] hover:shadow-md"
      style={{ background: PAPER.card, border: `1px solid ${PAPER.border}`, boxShadow: PAPER.shadow, opacity: voided ? 0.6 : 1 }}
      data-testid={`cost-log-row-${row.id}`}
    >
      <AuthThumb {...rowThumbProps(row)} className="w-12 h-12 rounded-lg flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-semibold truncate" style={{ color: PAPER.ink }} data-testid={`cost-log-row-supplier-${row.id}`}>
          {row.supplier_name || 'Unknown supplier'}
        </div>
        <div className="text-[11px] truncate flex items-center gap-1.5" style={{ color: PAPER.muted }}>
          <span>{dateLabel}</span>
          {row.reference && <span className="font-mono">· {row.reference}</span>}
          {row.category !== 'material' && <span className="uppercase tracking-wider font-bold text-[9px]">· {row.category}</span>}
          {row.source === 'inbox' ? <span data-testid={`cost-log-row-source-${row.id}`}>· invoice{row.inbox_has_file ? ' · PDF' : ''}</span> : row.source !== 'capture' && <span>· {row.source.replace('_', ' ')}</span>}
        </div>
        <div className="text-[11px] truncate" style={{ color: PAPER.hint }}>{row.captured_by_name || ''}</div>
      </div>
      <div className="text-right flex-shrink-0">
        {row.value_pending ? (
          <span className="text-[11px] font-bold px-1.5 py-0.5 rounded" style={{ background: '#FEF3C7', color: '#92400E' }} data-testid={`cost-log-row-value-${row.id}`}>Pending</span>
        ) : (
          <div className="text-[15px] font-bold tabular-nums" style={{ color: PAPER.ink }} data-testid={`cost-log-row-value-${row.id}`}>{fmtGbp(rowPence(row, basis) / 100)}</div>
        )}
        {!row.value_pending && (row.vat_pence || 0) > 0 && <div className="text-[9px] tabular-nums" style={{ color: PAPER.hint }}>{basis === 'net' ? `+ ${fmtGbp(row.vat_pence / 100)} VAT` : `${fmtGbp(row.net_pence / 100)} ex VAT`}</div>}
        {voided && <div className="text-[9px] uppercase font-bold" style={{ color: '#B91C1C' }}>Voided</div>}
      </div>
    </button>
  );
}

export default ProjectCostLog;
